import React from 'react';
import type { GetFeaturesInnerParams } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/types.ts';

export const getFlexibleDatesUSPMarkup = (
  params: GetFeaturesInnerParams,
  text?: string | null
): string | null | undefined => {
  const { isMobile, t, showFlexibleAlternativeDates, isOverlay } = params;
  const mobileUsp = t('vacation.oss.alternative_dates.usp_mobile');
  const desktopUsp = t('vacation.oss.alternative_dates.usp');

  if (showFlexibleAlternativeDates && !isOverlay) {
    return `${text} · <span class="more">${isMobile ? mobileUsp : desktopUsp}</span>`;
  }

  return text;
};

export const getDatesOnlyLabel = (params: GetFeaturesInnerParams): string | null | undefined => {
  const {
    nightsText,
    nightsTextStay,
    departureDate,
    checkinDate,
    isHO,
    isFlexible,
    isPartialStay,
    isOvernight,
    hasApexFlexibleDates,
  } = params;
  const { isOverlay, isMobile } = params;
  // The compact mobile map overlay uses the short form ("2 nights", no "at hotel").
  const nightsStayLabel = isOverlay && isMobile ? nightsText : nightsTextStay;
  // MPH-945: once the list mixes departure dates, the date is what separates one result from another,
  // so it has to read before the price. Feature text is rendered as markup.
  const emphasise = (date?: string) => (hasApexFlexibleDates && date ? `<b>${date}</b>` : date);

  // "13 Oct 2024 · 2 nights", HO and DP alike. An open search can arrive without a date to lead with.
  if (isFlexible) return departureDate ? `${departureDate} · ${nightsText}` : nightsText;
  if (isHO) return undefined;
  if (isOvernight || isPartialStay) return `${emphasise(checkinDate ?? departureDate)} · ${nightsStayLabel}`; // "14 Oct 2024 · 2 nights at hotel"
  if (departureDate) return `${emphasise(departureDate)} · ${nightsStayLabel}`; // "13 Oct 2024 · 2 nights at hotel"
  return nightsStayLabel; // "2 nights at hotel"
};

/**
 * MPH-945: the date segment of a `getDatesOnlyLabel` string, ready to render as a React child.
 *
 * The label is normally handed to `dangerouslySetInnerHTML`, so the emphasis travels as a `<b>` tag.
 * The overnight and partial-stay features instead split the label and render the halves as children,
 * which would escape that tag and show it to the user, so the marker is turned back into an element.
 */
export const renderDatePart = (datePart?: string): React.ReactNode => {
  const plain = datePart?.replace(/<\/?b>/g, '');

  return plain === datePart ? datePart : <b>{plain}</b>;
};
